import { useState, useEffect } from 'react';
import Menu from '../../components/Menu/Menu';
import Footer from '../../components/Footer';
import Toast from '../../components/Toast';
import { getBalance, getSwapQuote, doSwap } from '../../services/ExchangeService';
import { getCoins } from '../../services/ExchangeService';
import { getCriptoInfo } from '../../services/AgentService';
import Select from 'react-select/creatable';
import './Swap.css';

function Swap() {
    const [fromAsset, setFromAsset] = useState('');
    const [toAsset, setToAsset] = useState('');
    const [amount, setAmount] = useState('');
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingQuote, setLoadingQuote] = useState(false);
    const [error, setError] = useState('');
    const [notification, setNotification] = useState({ type: '', text: '' });
    const [userBalance, setUserBalance] = useState({});
    const [availableCoins, setAvailableCoins] = useState([]);
    const [fromCryptoInfo, setFromCryptoInfo] = useState(null);
    const [toCryptoInfo, setToCryptoInfo] = useState(null);
    const [loadingCoins, setLoadingCoins] = useState(true);

    // Carrega saldo e moedas disponíveis
    useEffect(() => {
        let isMounted = true;
        
        async function loadData() {
            try {
                await Promise.all([
                    loadUserBalance(),
                    loadAvailableCoins()
                ]);
            } catch (err) {
                console.error('Erro ao carregar dados iniciais:', err);
            }
        }
        
        loadData();
        
        return () => {
            isMounted = false;
        };
    }, []);

    // Carrega quote quando mudam os valores
    useEffect(() => {
        if (fromAsset && toAsset && amount && parseFloat(amount) > 0) {
            const timer = setTimeout(() => {
                loadQuote();
            }, 500); // Debounce de 500ms
            return () => clearTimeout(timer);
        } else {
            setQuote(null);
        }
    }, [fromAsset, toAsset, amount]);

    // Carrega informações das criptos
    useEffect(() => {
        if (fromAsset) {
            loadCryptoInfo(fromAsset, setFromCryptoInfo);
        }
    }, [fromAsset]);

    useEffect(() => {
        if (toAsset) {
            loadCryptoInfo(toAsset, setToCryptoInfo);
        }
    }, [toAsset]);

    async function loadUserBalance() {
        try {
            const balance = await getBalance(false);
            // Converte para objeto simples { COIN: { available: X } } - igual à página Trade
            const balanceObj = {};
            if (Array.isArray(balance)) {
                balance.forEach(item => {
                    balanceObj[item.asset || item.symbol] = {
                        available: item.available || item.availableBalance || 0
                    };
                });
            } else {
                Object.entries(balance).forEach(([key, value]) => {
                    if (key !== 'fiatEstimate') {
                        balanceObj[key] = {
                            available: value.available || value.availableBalance || 0
                        };
                    }
                });
            }
            setUserBalance(balanceObj);
        } catch (err) {
            console.error('Erro ao carregar saldo:', err);
            setNotification({ type: 'error', text: 'Erro ao carregar saldo da carteira.' });
        }
    }

    async function loadAvailableCoins() {
        setLoadingCoins(true);
        try {
            console.log('Iniciando carregamento de moedas...');
            const coins = await getCoins();
            console.log('Resposta da API getCoins:', coins);
            
            if (Array.isArray(coins) && coins.length > 0) {
                const coinOptions = coins
                    .filter(coin => coin && coin.coin) // Filtra moedas válidas
                    .map(coin => ({
                        value: coin.coin, // Mantém o formato original da API
                        label: coin.coin
                    }));
                setAvailableCoins(coinOptions);
            } else {
                console.warn('Nenhuma moeda retornada da API ou formato inválido');
                setAvailableCoins([]);
                setNotification({ type: 'warning', text: 'Nenhuma moeda disponível para swap.' });
            }
        } catch (err) {
            console.error('Erro ao carregar moedas:', err);
            const errorMsg = err.response?.data || err.message || 'Erro desconhecido';
            setNotification({ type: 'error', text: `Erro ao carregar lista de moedas: ${errorMsg}` });
            setAvailableCoins([]);
        } finally {
            setLoadingCoins(false);
        }
    }

    async function loadCryptoInfo(asset, setInfo) {
        try {
            const info = await getCriptoInfo(asset);
            setInfo(info);
        } catch (err) {
            console.error('Erro ao carregar info da cripto:', err);
            setInfo(null);
        }
    }

    async function loadQuote() {
        if (!fromAsset || !toAsset || !amount || parseFloat(amount) <= 0) {
            setQuote(null);
            return;
        }

        setLoadingQuote(true);
        setError('');
        try {
            const quoteData = await getSwapQuote(fromAsset, toAsset, amount);
            console.log('Resposta da API de cotação (completa):', JSON.stringify(quoteData, null, 2));
            
            // Processa a resposta para garantir que temos os campos corretos
            if (quoteData) {
                // A API pode retornar diferentes formatos
                const processedQuote = {
                    quoteQty: quoteData.quoteQty || quoteData.quoteAmount || quoteData.toAmount || quoteData.quoteQuantity || 0,
                    ratio: quoteData.ratio || quoteData.price || quoteData.exchangeRate || 0,
                    fee: quoteData.fee || quoteData.estimatedFee || quoteData.commission || 0,
                    fromAmount: quoteData.fromAmount || amount,
                    toAmount: quoteData.quoteQty || quoteData.quoteAmount || quoteData.toAmount || quoteData.quoteQuantity || 0
                };
                console.log('Cotação processada:', processedQuote);
                setQuote(processedQuote);
            } else {
                setQuote(null);
            }
        } catch (err) {
            console.error('Erro ao carregar cotação:', err);
            setError(err.response?.data || err.message || 'Erro ao obter cotação');
            setQuote(null);
        } finally {
            setLoadingQuote(false);
        }
    }

    function handleFromAssetChange(selected) {
        setFromAsset(selected?.value || '');
        setQuote(null);
        setError('');
    }

    function handleToAssetChange(selected) {
        setToAsset(selected?.value || '');
        setQuote(null);
        setError('');
    }

    function handleAmountChange(e) {
        const value = e.target.value;
        // Permite apenas números e ponto decimal
        if (value === '' || /^\d*\.?\d*$/.test(value)) {
            setAmount(value);
            setError('');
        }
    }

    function handleMaxClick() {
        if (fromAsset && userBalance[fromAsset]) {
            const available = parseFloat(userBalance[fromAsset].available || 0);
            const maxAmount = fromAsset === 'BNB'
                ? available * 0.999
                : available;
            setAmount(maxAmount > 0 ? maxAmount.toString() : '0');
        }
    }

    function handleSwapAssets() {
        const temp = fromAsset;
        setFromAsset(toAsset);
        setToAsset(temp);
        setQuote(null);
        setError('');
    }

    async function handleSwap() {
        if (!fromAsset || !toAsset || !amount || parseFloat(amount) <= 0) {
            setNotification({ type: 'error', text: 'Preencha todos os campos' });
            return;
        }

        if (fromAsset === toAsset) {
            setNotification({ type: 'error', text: 'As moedas devem ser diferentes' });
            return;
        }

        const available = parseFloat(userBalance[fromAsset]?.available || 0);
        let swapAmount = parseFloat(amount);
        if (fromAsset === 'BNB' && swapAmount > available * 0.998)
            swapAmount = available * 0.999;
        if (swapAmount > available) {
            setNotification({ type: 'error', text: `Saldo insuficiente. Você tem ${available.toFixed(8)} ${fromAsset}` });
            return;
        }

        setLoading(true);
        setError('');
        try {
            console.log('Iniciando swap:', { fromAsset, toAsset, amount: swapAmount });
            const result = await doSwap(fromAsset, toAsset, swapAmount);
            console.log('Resultado do swap:', result);
            
            // Processa a resposta (pode ser string JSON ou objeto)
            let resultData = result;
            if (typeof result === 'string') {
                try {
                    resultData = JSON.parse(result);
                } catch (e) {
                    // Se não for JSON válido, usa como está
                }
            }
            
            // Verifica se houve erro na resposta
            if (resultData && (resultData.code || resultData.msg || resultData.status === 'FAILED')) {
                const errorMsg = resultData.msg || resultData.message || 'Falha ao executar o swap';
                setNotification({ type: 'error', text: errorMsg });
                setError(errorMsg);
            } else {
                // Sucesso
                const successMsg = resultData?.status === 'SUCCESS' 
                    ? `Swap executado via compra/venda de mercado. Você recebeu ${resultData.toAmount || amount} ${toAsset}`
                    : 'Swap executado via compra/venda de mercado.';
                setNotification({ type: 'success', text: successMsg });
                setAmount('');
                setQuote(null);
                // Recarrega o saldo após um pequeno delay para garantir que a API atualizou
                setTimeout(async () => {
                    await loadUserBalance();
                }, 1000);
            }
        } catch (err) {
            console.error('Erro ao fazer swap:', err);
            let errorMsg = 'Falha ao executar o swap';
            let showSuggestion = false;
            
            if (err.response) {
                // Erro da API
                const data = err.response.data;
                if (typeof data === 'string') {
                    try {
                        const parsed = JSON.parse(data);
                        errorMsg = parsed.msg || parsed.message || data;
                        showSuggestion = parsed.unsupported || parsed.suggestion;
                    } catch (e) {
                        errorMsg = data;
                    }
                } else if (data) {
                    if (data.msg) {
                        errorMsg = data.msg;
                    } else if (data.message) {
                        errorMsg = data.message;
                    } else {
                        errorMsg = JSON.stringify(data);
                    }
                    showSuggestion = data.unsupported || data.suggestion;
                }
            } else if (err.message) {
                errorMsg = err.message;
            }
            
            // Se for erro de não suportado, adiciona sugestão
            if (showSuggestion && errorMsg.includes('não é suportada')) {
                errorMsg += ' Use a página de Comprar/Vender para realizar esta operação.';
            }
            
            setNotification({ type: 'error', text: errorMsg });
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    }

    const customStyles = {
        control: (provided, state) => ({
            ...provided,
            width: '100%',
            backgroundColor: 'rgba(26, 26, 26, 0.8)',
            border: state.isFocused ? '1px solid #8c66fc' : '1px solid rgba(140, 102, 252, 0.3)',
            borderRadius: '10px',
            boxShadow: state.isFocused ? '0 0 0 3px rgba(140, 102, 252, 0.2)' : 'none',
            minHeight: '48px',
            '&:hover': {
                border: '1px solid #8c66fc'
            }
        }),
        menu: (provided) => ({
            ...provided,
            backgroundColor: '#252525',
            border: '1px solid rgba(140, 102, 252, 0.3)',
            borderRadius: '10px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
            zIndex: 9999
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected 
                ? 'rgba(140, 102, 252, 0.3)' 
                : state.isFocused 
                    ? 'rgba(140, 102, 252, 0.2)' 
                    : 'rgba(26, 26, 26, 0.5)',
            color: state.isSelected || state.isFocused ? '#ffffff' : '#e0e0e0',
            cursor: 'pointer'
        }),
        singleValue: (provided) => ({
            ...provided,
            color: '#ffffff',
            fontWeight: 600
        }),
        placeholder: (provided) => ({
            ...provided,
            color: '#a0a0a0'
        }),
        input: (provided) => ({
            ...provided,
            color: '#ffffff'
        })
    };

    // Filtra moedas disponíveis para "De"
    // Mostra apenas moedas que o usuário possui (com saldo > 0)
    const fromOptions = availableCoins.filter(coin => {
        if (!coin || !coin.value) return false;
        const balance = userBalance[coin.value];
        return balance && parseFloat(balance.available || 0) > 0;
    });

    // Filtra moedas disponíveis para "Para"
    // Mostra todas as moedas disponíveis, exceto a selecionada em "De"
    const toOptions = availableCoins.filter(coin => {
        if (!coin || !coin.value) return false;
        return coin.value !== fromAsset;
    });


    return (
        <>
            <Menu />
            <main className="content">
                <div className="swap-container">
                    <div className="swap-header">
                        <h2>Swap de Criptomoedas</h2>
                        <p className="swap-subtitle">Troque uma criptomoeda por outra instantaneamente</p>
                    </div>

                    <div className="swap-content">
                        {/* Card: De */}
                        <div className="swap-card">
                            <div className="swap-card-header">
                                <h3>De</h3>
                                {fromAsset && userBalance[fromAsset] && (
                                    <span className="balance-info">
                                        Disponível: {parseFloat(userBalance[fromAsset].available || 0).toFixed(8)} {fromAsset}
                                    </span>
                                )}
                            </div>
                            <div className="swap-card-body">
                                <div className="swap-asset-selector">
                                    {fromCryptoInfo && (
                                        <img 
                                            src={fromCryptoInfo.image} 
                                            alt={fromCryptoInfo.name}
                                            className="crypto-icon"
                                            onError={(e) => {
                                                e.target.src = 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@bea1a9722a8c63169dcc06e86182bf2c55a76bbc/128/color/generic.png';
                                            }}
                                        />
                                    )}
                                    <Select
                                        value={fromOptions.find(opt => opt.value === fromAsset) || null}
                                        onChange={handleFromAssetChange}
                                        options={fromOptions.length > 0 ? fromOptions : []}
                                        placeholder={loadingCoins ? "Carregando moedas..." : (fromOptions.length === 0 ? "Nenhuma moeda disponível na carteira" : "Selecione a moeda")}
                                        styles={customStyles}
                                        isSearchable
                                        isDisabled={loadingCoins || fromOptions.length === 0}
                                        noOptionsMessage={() => loadingCoins ? "Carregando..." : "Nenhuma moeda disponível na carteira"}
                                    />
                                </div>
                                <div className="swap-amount-input">
                                    <input
                                        type="text"
                                        value={amount}
                                        onChange={handleAmountChange}
                                        placeholder="0.00"
                                        className="swap-input"
                                    />
                                    <button 
                                        className="swap-max-btn"
                                        onClick={handleMaxClick}
                                        disabled={!fromAsset || !userBalance[fromAsset]}
                                    >
                                        MAX
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Botão de trocar */}
                        <div className="swap-switch-container">
                            <button 
                                className="swap-switch-btn"
                                onClick={handleSwapAssets}
                                disabled={!fromAsset || !toAsset}
                            >
                                <svg className="icon icon-lg" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M8 5a1 1 0 100 2h5.586l-1.293 1.293a1 1 0 101.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L13.586 5H8zM12 15a1 1 0 110-2H6.414l1.293-1.293a1 1 0 10-1.414-1.414l-3 3a1 1 0 000 1.414l3 3a1 1 0 001.414-1.414L6.414 15H12z" />
                                </svg>
                            </button>
                        </div>

                        {/* Card: Para */}
                        <div className="swap-card">
                            <div className="swap-card-header">
                                <h3>Para</h3>
                            </div>
                            <div className="swap-card-body">
                                <div className="swap-asset-selector">
                                    {toCryptoInfo && (
                                        <img 
                                            src={toCryptoInfo.image} 
                                            alt={toCryptoInfo.name}
                                            className="crypto-icon"
                                            onError={(e) => {
                                                e.target.src = 'https://cdn.jsdelivr.net/gh/atomiclabs/cryptocurrency-icons@bea1a9722a8c63169dcc06e86182bf2c55a76bbc/128/color/generic.png';
                                            }}
                                        />
                                    )}
                                    <Select
                                        value={toOptions.find(opt => opt.value === toAsset) || null}
                                        onChange={handleToAssetChange}
                                        options={toOptions}
                                        placeholder={loadingCoins ? "Carregando moedas..." : (toOptions.length === 0 ? "Nenhuma moeda disponível" : "Selecione a moeda")}
                                        styles={customStyles}
                                        isSearchable
                                        isDisabled={loadingCoins || toOptions.length === 0}
                                        noOptionsMessage={() => loadingCoins ? "Carregando..." : "Nenhuma moeda disponível"}
                                    />
                                </div>
                                <div className="swap-amount-display">
                                    {loadingQuote ? (
                                        <div className="swap-loading">Calculando...</div>
                                    ) : quote ? (
                                        <div className="swap-quote">
                                            <div className="swap-quote-amount">
                                                {parseFloat(quote.toAmount || quote.quoteQty || quote.quoteAmount || 0).toFixed(8)}
                                            </div>
                                            <div className="swap-quote-rate">
                                                {parseFloat(amount || 0) > 0 && parseFloat(quote.toAmount || quote.quoteQty || 0) > 0 ? (
                                                    <>
                                                        1 {fromAsset} = {((parseFloat(quote.toAmount || quote.quoteQty || 0) / parseFloat(amount || 1))).toFixed(8)} {toAsset}
                                                    </>
                                                ) : (
                                                    <>Calculando taxa...</>
                                                )}
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="swap-placeholder">-</div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Informações do swap */}
                        {quote && (
                            <div className="swap-info-card">
                                <div className="swap-info-item">
                                    <span className="swap-info-label">Taxa estimada:</span>
                                    <span className="swap-info-value">{parseFloat(quote.fee || quote.estimatedFee || 0).toFixed(8)} {toAsset}</span>
                                </div>
                                <div className="swap-info-item">
                                    <span className="swap-info-label">Você receberá:</span>
                                    <span className="swap-info-value highlight">
                                        {parseFloat(quote.quoteQty || quote.toAmount || quote.quoteAmount || 0).toFixed(8)} {toAsset}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Botão de swap */}
                        <button
                            className="swap-submit-btn"
                            onClick={handleSwap}
                            disabled={loading || !fromAsset || !toAsset || !amount || parseFloat(amount) <= 0 || !quote}
                        >
                            {loading ? 'Processando...' : 'Fazer Swap'}
                        </button>

                        {error && (
                            <div className="swap-error">
                                {error}
                            </div>
                        )}
                    </div>
                </div>
            </main>
            <Footer />
            <Toast notification={notification} setNotification={setNotification} />
        </>
    );
}

export default Swap;

